import { Box, Button, Icon, IconType } from 'lucid-components-react';
import React, { useState, ReactElement } from 'react';

import { LucidTheme } from 'types/types';

type PaginatorState = {
  activePage: number;
  visibleItems: any;
}

type PaginatorProps = {
  alwaysRender?: boolean;
  items: JSX.Element[];
  pageSize: number;
  children: (props: PaginatorState) => ReactElement<any>;
  viewMoreText?: string;
}

const Paginator = ({
  alwaysRender = false,
  children,
  items,
  pageSize,
  viewMoreText
}: PaginatorProps) => {
  const [activePage, setActivePage] = useState(1);
  let visibleItems = items.slice(0, activePage * pageSize);
  const totalItemCount = items.length;
  const hasMore = totalItemCount > pageSize * activePage;

  if (alwaysRender) {
    visibleItems = items.map((item, i) => {
      if (i >= activePage * pageSize) {
        return React.cloneElement(item, {
          key: i,
          css: {
            display: alwaysRender ? 'block' : 'none',
            position: 'relative'
          },
          'data-grid-class': 'paginator__hidden'
        });
      }

      return item;
    });
  }

  return (
    <Box
      css={{
        label: 'paginator'
      }}
    >
      {children({ activePage, visibleItems })}
      {hasMore && (
        <Box
          css={(theme: LucidTheme) => ({
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            position: 'relative',
            padding: `${theme.space[6]}px auto`,
            alignItems: 'center'
          })}
        >
          <hr
            css={(theme: LucidTheme) => ({
              border: 0,
              borderBottom: `2px solid ${theme.colors.coolGrey[10]}`,
              margin: 0,
              width: '100%'
            })}
          />
          <Button
            asLink
            css={(theme: LucidTheme) => ({
              cursor: 'pointer',
              padding: theme.space[2],
              whiteSpace: 'nowrap',
              background: 'transparent',
              border: 0,
              fontSize: theme.fontSizes[3],
              color: theme.colors.orange[45],
              '&:visited': {
                color: theme.colors.orange[70]
              },
              '&:hover': {
                color: theme.colors.orange[60]
              }
            })}
            onClick={() => setActivePage(activePage + 1)}
          >
            {viewMoreText}
            <Box
              css={(theme: LucidTheme) => ({
                display: 'inline-block',
                fontSize: theme.fontSizes[2],
                paddingLeft: theme.space[2]
              })}
            >
              <Icon type={IconType.ArrowRight} />
            </Box>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Paginator;
